import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';

const Overlay = styled.div`position: fixed; inset: 0; background: rgba(2,6,23,.55); z-index: 1200;`;
const Panel = styled.div`
  position: fixed; top: 0; right: 0; bottom: 0; width: min(520px, 94vw);
  background: #0b1220; border-left: 1px solid rgba(148,163,184,.18); z-index: 1201;
  overflow-y: auto; padding: 22px; color: #e2e8f0;
`;
const Head = styled.div`display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px;`;
const Close = styled.button`background: transparent; border: none; color: #94a3b8; font-size: 22px; cursor: pointer;`;
const Section = styled.div`font-size: 12px; text-transform: uppercase; letter-spacing: .05em; color: #94a3b8; margin: 18px 0 8px; border-bottom: 1px solid rgba(148,163,184,.14); padding-bottom: 6px;`;
const Grid = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 10px 18px;`;
const Cell = styled.div`.k { color: #94a3b8; font-size: 11px; } .v { color: #f8fafc; font-size: 14px; margin-top: 2px; }`;
const Badge = styled.span<{ $tone?: string }>`
  display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 700;
  background: ${p => p.$tone === 'agency' ? 'rgba(34,197,94,.16)' : 'rgba(139,92,246,.16)'};
  color: ${p => p.$tone === 'agency' ? '#86efac' : '#c4b5fd'};
`;

const pct = (v: any) => v == null ? '--' : `${(Number(v) * (Math.abs(Number(v)) <= 1 ? 100 : 1)).toFixed(2)}%`;
const money = (v: any) => v == null ? '--' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 2 }).format(Number(v));
const plainPct = (v: any) => v == null ? '--' : `${Number(v).toFixed(2)}%`;

const Field: React.FC<{ k: string; v: any }> = ({ k, v }) => (
  <Cell><div className="k">{k}</div><div className="v">{v == null || v === '' ? '--' : v}</div></Cell>
);

const Btn = styled.button`
  border: 1px solid rgba(56,189,248,.4); background: rgba(14,116,144,.22); color: #bae6fd;
  border-radius: 7px; padding: 6px 10px; font-size: 12px; font-weight: 700; cursor: pointer;
`;
const Inp = styled.input`
  width: 70px; background: #111827; border: 1px solid rgba(148,163,184,.24); color: #f8fafc;
  border-radius: 6px; padding: 5px 7px; font-size: 13px;
`;
const Tbl = styled.table`
  width: 100%; border-collapse: collapse; margin-top: 8px;
  th, td { text-align: right; padding: 5px 6px; font-size: 12px; border-bottom: 1px solid rgba(148,163,184,.1); }
  th { color: #94a3b8; font-weight: 600; }
  th:first-child, td:first-child { text-align: left; }
`;

const RmbsTrancheDrawer: React.FC<{ positionId: number; onClose: () => void }> = ({ positionId, onClose }) => {
  const [detail, setDetail] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<any | null>(null);
  const [mktRate, setMktRate] = useState('6.5');
  const [cdr, setCdr] = useState('');
  const [severity, setSeverity] = useState('');
  const [running, setRunning] = useState(false);

  const runAnalytics = (rate: string, cdrV?: string, sevV?: string) => {
    setRunning(true);
    const params: any = { marketRatePct: rate };
    if (cdrV) params.cdrPct = cdrV;
    if (sevV) params.severityPct = sevV;
    axios.get(`/api/credit/rmbs/positions/${positionId}/analytics`, { params })
      .then(res => setAnalytics(res.data))
      .catch(() => setAnalytics({ error: true }))
      .finally(() => setRunning(false));
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios.get(`/api/credit/rmbs/positions/${positionId}/tranche-detail`)
      .then(res => { if (active) setDetail(res.data); })
      .catch(() => { if (active) setDetail({ found: false }); })
      .finally(() => { if (active) setLoading(false); });
    runAnalytics('6.5');
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionId]);

  const d = detail || {};
  const agencyTone = d.agency && d.agency !== 'NON_AGENCY' ? 'agency' : 'pls';

  return (
    <>
      <Overlay onClick={onClose} />
      <Panel>
        <Head>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{d.dealName || 'RMBS Tranche'} {d.trancheName ? `· ${d.trancheName}` : ''}</div>
            {d.agency && <div style={{ marginTop: 6 }}><Badge $tone={agencyTone}>{d.agency}</Badge> <span style={{ color: '#94a3b8', fontSize: 12 }}>{d.dealType}</span></div>}
          </div>
          <Close onClick={onClose}>×</Close>
        </Head>

        {loading ? <div>Loading…</div> : d.found === false ? <div style={{ color: '#64748b' }}>No tranche detail for this position.</div> : (
          <>
            <Section>Tranche</Section>
            <Grid>
              <Field k="Class" v={d.trancheClass} />
              <Field k="Structure" v={d.structureType} />
              <Field k="Payment Priority" v={d.paymentPriority} />
              <Field k="CUSIP" v={d.cusip} />
              <Field k="Coupon" v={d.couponType ? `${d.couponType} ${d.couponRate ? plainPct(d.couponRate) : ''}` : '--'} />
              <Field k="Spread (bps)" v={d.spreadBps} />
              <Field k="Original Balance" v={money(d.originalBalance)} />
              <Field k="Current Balance" v={money(d.currentBalance)} />
              <Field k="WAL (yrs)" v={d.weightedAvgLifeYears} />
              <Field k="IO" v={d.isIo ? 'Yes' : 'No'} />
            </Grid>

            <Section>Credit Support &amp; Ratings</Section>
            <Grid>
              <Field k="Attachment" v={pct(d.attachmentPoint)} />
              <Field k="Detachment" v={pct(d.detachmentPoint)} />
              <Field k="Orig Credit Support" v={pct(d.originalCreditSupportPct)} />
              <Field k="Curr Credit Support" v={pct(d.currentCreditSupportPct)} />
              <Field k="Moody's" v={d.moodysRating} />
              <Field k="S&P" v={d.spRating} />
              <Field k="Fitch" v={d.fitchRating} />
              <Field k="Legal Final" v={d.legalFinalMaturity} />
            </Grid>

            <Section>Collateral Pool</Section>
            <Grid>
              <Field k="Servicer" v={d.servicerName} />
              <Field k="Trustee" v={d.trusteeName} />
              <Field k="Product" v={d.productType} />
              <Field k="Pool WAC" v={plainPct(d.poolWac)} />
              <Field k="WA FICO" v={d.poolWaFico} />
              <Field k="WA LTV" v={plainPct(d.poolWaLtv)} />
              <Field k="3M CPR" v={plainPct(d.poolCpr3m)} />
              <Field k="CDR" v={plainPct(d.poolCdr)} />
              <Field k="90+ DQ" v={plainPct(d.poolDelinquency90Plus)} />
            </Grid>

            <Section>Prepayment &amp; Risk {analytics?.provider && <span style={{ textTransform: 'none', color: '#64748b' }}>· {analytics.provider} model</span>}</Section>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
              <span style={{ color: '#94a3b8', fontSize: 12 }}>Mkt rate %</span>
              <Inp value={mktRate} onChange={(e) => setMktRate(e.target.value)} />
              {analytics?.credit?.creditApplicable && (
                <>
                  <span style={{ color: '#94a3b8', fontSize: 12 }}>CDR %</span>
                  <Inp value={cdr} placeholder="auto" onChange={(e) => setCdr(e.target.value)} />
                  <span style={{ color: '#94a3b8', fontSize: 12 }}>Sev %</span>
                  <Inp value={severity} placeholder="35" onChange={(e) => setSeverity(e.target.value)} />
                </>
              )}
              <Btn onClick={() => runAnalytics(mktRate, cdr, severity)} disabled={running}>{running ? 'Running…' : 'Run'}</Btn>
            </div>
            {analytics && !analytics.error && analytics.metrics && (
              <>
                <Grid>
                  <Field k="Base CPR" v={plainPct(analytics.metrics.baseCaseCprPct)} />
                  <Field k="WAL (yrs)" v={analytics.metrics.walYears} />
                  <Field k="Yield" v={plainPct(analytics.metrics.yieldPct)} />
                  <Field k="Eff. Duration" v={analytics.metrics.effectiveDuration} />
                  <Field k="Eff. Convexity" v={analytics.metrics.effectiveConvexity} />
                  <Field k="Z-Spread (bps)" v={analytics.metrics.zSpreadBps} />
                  <Field k="OAS" v={analytics.metrics.oas == null ? '— (Intex)' : analytics.metrics.oas} />
                  <Field k="Refi Incentive" v={plainPct(analytics.assumptions?.refiIncentivePct)} />
                </Grid>
                <Tbl>
                  <thead><tr><th>Rate Shock</th><th>CPR</th><th>WAL</th><th>Price</th><th>Δ Price</th></tr></thead>
                  <tbody>
                    {(analytics.scenarios || []).map((s: any) => (
                      <tr key={s.rateShockBps}>
                        <td style={{ fontWeight: s.rateShockBps === 0 ? 700 : 400 }}>{s.rateShockBps > 0 ? '+' : ''}{s.rateShockBps} bp</td>
                        <td>{plainPct(s.cprPct)}</td>
                        <td>{s.walYears}y</td>
                        <td>{Number(s.pricePct).toFixed(2)}</td>
                        <td style={{ color: s.priceChangePct < 0 ? '#f87171' : '#34d399' }}>{s.priceChangePct > 0 ? '+' : ''}{Number(s.priceChangePct).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Tbl>
                <div style={{ color: '#64748b', fontSize: 11, marginTop: 6 }}>
                  Transparent refi-incentive model. OAS &amp; deal-accurate cash flows via Intex integration (roadmap).
                </div>
              </>
            )}

            {analytics?.credit && (
              analytics.credit.creditApplicable ? (
                <>
                  <Section>Credit &amp; Loss <span style={{ textTransform: 'none', color: '#64748b' }}>· non-agency</span></Section>
                  <Grid>
                    <Field k="Breakeven CDR" v={analytics.credit.breakevenCdrPct == null ? '>99% (covered)' : plainPct(analytics.credit.breakevenCdrPct)} />
                    <Field k="Expected Loss" v={plainPct(analytics.credit.expectedLossToTranchePct)} />
                    <Field k="Cum. Pool Loss" v={plainPct(analytics.credit.cumulativePoolLossPct)} />
                    <Field k="Loss-Adj Yield" v={plainPct(analytics.credit.lossAdjustedYieldPct)} />
                    <Field k="Yield Give-up" v={`${analytics.credit.yieldGiveUpBps} bps`} />
                    <Field k="Attach / Detach" v={`${analytics.credit.attachmentPct}% / ${analytics.credit.detachmentPct}%`} />
                    <Field k="Assumed CDR" v={plainPct(analytics.credit.assumedCdrPct)} />
                    <Field k="Assumed Severity" v={plainPct(analytics.credit.assumedSeverityPct)} />
                  </Grid>
                  <div style={{ color: '#94a3b8', fontSize: 11, margin: '8px 0 2px' }}>Tranche loss % by CDR × severity</div>
                  <Tbl>
                    <thead><tr><th>CDR</th><th>25% sev</th><th>35% sev</th><th>45% sev</th></tr></thead>
                    <tbody>
                      {(analytics.credit.scenarioGrid || []).map((r: any) => (
                        <tr key={r.cdrPct}>
                          <td style={{ fontWeight: 600 }}>{r.cdrPct}%</td>
                          {(r.cells || []).map((cell: any) => (
                            <td key={cell.severityPct} style={{ color: cell.trancheLossPct > 0 ? '#f87171' : '#64748b' }}>
                              {Number(cell.trancheLossPct).toFixed(1)}%
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Tbl>
                  <div style={{ color: '#64748b', fontSize: 11, marginTop: 6 }}>
                    Transparent loss model (CDR×severity → pool loss → allocated by attach/detach). Deal-exact waterfall via Intex.
                  </div>
                </>
              ) : (
                <>
                  <Section>Credit</Section>
                  <div style={{ color: '#86efac', fontSize: 12 }}>Agency-guaranteed — no credit loss to the bondholder; prepayment is the only risk.</div>
                </>
              )
            )}
            {running && !analytics && <div style={{ color: '#64748b', fontSize: 12 }}>Computing…</div>}
          </>
        )}
      </Panel>
    </>
  );
};

export default RmbsTrancheDrawer;
